import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { NodeSelection } from 'prosemirror-state';
import type { ReactNode } from 'react';
import { useContext, useState } from 'react';
import { Button } from '../../atom/Button/Button';
import { Input } from '../../atom/Input/Input';
import { InputDecorator } from '../../atom/InputDecorator/InputDecorator';
import { Modal } from '../../atom/Modal/Modal';
import { Radio } from '../../atom/Radio/Radio';
import { CustomEditorContext } from '.';
import BoxedTextIcon from './_assets/boxed-text.svg';
import HrIcon from './_assets/hr.svg';
import type { ImageButtonProps } from './_components/ImageButton';
import { ImageButton } from './_components/ImageButton';
import { TEXT_ALIGN } from './_constants';
import { CLASS_TO_BUTTON_SIZE, CLASS_TO_BUTTON_THEME, type CustomLinkOptions } from './_utils/CustomLink';
import { FONT_SIZES, type FontSizeType } from './_utils/FontSize';
import { FONT_WEIGHTS } from './_utils/FontWeight';
import { HIGHLIGHTS } from './_utils/Highlight';
import { TEXT_COLORS } from './_utils/TextColor';
export interface ToolbarProps {
  imageUploadOptions?: ImageButtonProps;
  children?: ReactNode;
}
type Level = 2 | 3 | 4 | 5 | 6;
const HEADING_OPTIONS = [{
  value: 'paragraph',
  label: '본문'
}, {
  value: 'h2',
  label: '제목 2'
}, {
  value: 'h3',
  label: '제목 3'
}, {
  value: 'h4',
  label: '제목 4'
}, {
  value: 'h5',
  label: '제목 5'
}, {
  value: 'h6',
  label: '제목 6'
}];
export const Toolbar = t0 => {
  const $ = _c(124);
  const {
    imageUploadOptions,
    children
  } = t0;
  const {
    editor
  } = useContext(CustomEditorContext);
  const [isLinkModalOpened, setIsLinkModalOpened] = useState(false);
  const isImageSelected = editor?.state.selection instanceof NodeSelection && editor?.state.selection.node.type.name === "image";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      theme: "text",
      size: "H56",
      isOutLink: false,
      href: ""
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [customLinkOptions, setCustomLinkOptions] = useState(t1);
  let t2;
  if ($[1] !== editor || $[2] !== isImageSelected) {
    t2 = () => {
      if (isImageSelected) {
        const imageAttrs = editor?.state.selection instanceof NodeSelection ? editor.state.selection.node.attrs : null;
        setCustomLinkOptions(prev => ({
          ...prev,
          theme: "image",
          href: imageAttrs?.href || "",
          isOutLink: imageAttrs?.target === "_blank"
        }));
      } else {
        if (editor?.isActive("link")) {
          const linkAttrs = editor.getAttributes("link");
          const linkClass = linkAttrs.class || "";
          const theme = Object.entries(CLASS_TO_BUTTON_THEME).find(t3 => {
            const [cls] = t3;
            return linkClass.includes(cls);
          })?.[1] as CustomLinkOptions['theme'] ?? "text";
          const size = Object.entries(CLASS_TO_BUTTON_SIZE).find(t4 => {
            const [cls_0] = t4;
            return linkClass.includes(cls_0);
          })?.[1] as CustomLinkOptions['size'] ?? "H56";
          setCustomLinkOptions({
            theme,
            size,
            href: linkAttrs.href || "",
            isOutLink: linkAttrs.target === "_blank"
          });
        } else {
          setCustomLinkOptions({
            theme: "text",
            size: "H56",
            href: "",
            isOutLink: false
          });
        }
      }
      setIsLinkModalOpened(true);
    };
    $[1] = editor;
    $[2] = isImageSelected;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleLinkButtonClick = t2;
  if (!editor) {
    return null;
  }
  let t3;
  if ($[4] !== editor) {
    t3 = () => {
      for (let i = 2; i <= 6; i++) {
        if (editor.isActive("heading", {
          level: i
        })) {
          return `h${i}`;
        }
      }
      return "paragraph";
    };
    $[4] = editor;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const getSelectedHeading = t3;
  const selectedHeading = getSelectedHeading();
  let t4;
  if ($[6] !== editor) {
    t4 = TEXT_COLORS.find(t5 => {
      const [, color] = t5;
      return editor.isActive("textStyle", {
        color
      });
    }) ?? [];
    $[6] = editor;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [selectedColor] = t4;
  let t5;
  if ($[8] !== editor) {
    t5 = FONT_WEIGHTS.find(fontWeight => editor.isActive("fontWeight", {
      fontWeight
    }));
    $[8] = editor;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const selectedFontWeight = t5;
  let t6;
  if ($[10] !== editor) {
    t6 = FONT_SIZES.find(fontSize => editor.isActive("fontSize", {
      fontSize
    }));
    $[10] = editor;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const selectedFontSize = t6;
  let isBoxedText;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t7;
  let t8;
  let t9;
  if ($[12] !== editor || $[13] !== selectedColor || $[14] !== selectedFontSize || $[15] !== selectedFontWeight || $[16] !== selectedHeading) {
    isBoxedText = editor.isActive("boxedText");
    let t16;
    if ($[27] !== editor) {
      t16 = TEXT_ALIGN.find(textAlign => editor.isActive("textAlign", {
        textAlign
      }));
      $[27] = editor;
      $[28] = t16;
    } else {
      t16 = $[28];
    }
    const selectedTextAlign = t16;
    t8 = "flex flex-wrap items-center justify-center gap-8 bg-Gray-100 p-8";
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <p className="text-Gray-800 typo-caption1">*h1 태그는 제목 고정입니다.</p>;
      $[29] = t9;
    } else {
      t9 = $[29];
    }
    let t17;
    if ($[30] !== editor) {
      t17 = e => {
        const value = e.target.value;
        if (value === "paragraph") {
          editor.chain().focus().setParagraph().run();
        } else {
          const level = Number.parseInt(value.substring(1)) as Level;
          editor.chain().focus().setHeading({
            level
          }).run();
        }
      };
      $[30] = editor;
      $[31] = t17;
    } else {
      t17 = $[31];
    }
    let t18;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = HEADING_OPTIONS.map(_temp);
      $[32] = t18;
    } else {
      t18 = $[32];
    }
    if ($[33] !== selectedHeading || $[34] !== t17) {
      t10 = <select value={selectedHeading} onChange={t17}>{t18}</select>;
      $[33] = selectedHeading;
      $[34] = t17;
      $[35] = t10;
    } else {
      t10 = $[35];
    }
    let t19;
    if ($[36] !== editor) {
      t19 = e_0 => {
        editor.chain().focus().setFontSize(e_0.target.value as FontSizeType).run();
      };
      $[36] = editor;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    let t20;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = FONT_SIZES.map(_temp2);
      $[38] = t20;
    } else {
      t20 = $[38];
    }
    if ($[39] !== selectedFontSize || $[40] !== t19) {
      t11 = <select value={selectedFontSize} onChange={t19}>{t20}</select>;
      $[39] = selectedFontSize;
      $[40] = t19;
      $[41] = t11;
    } else {
      t11 = $[41];
    }
    let t21;
    if ($[42] !== editor) {
      t21 = e_1 => {
        editor?.chain().focus().setTextAlign(e_1.target.value).run();
      };
      $[42] = editor;
      $[43] = t21;
    } else {
      t21 = $[43];
    }
    let t22;
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = TEXT_ALIGN.map(_temp3);
      $[44] = t22;
    } else {
      t22 = $[44];
    }
    if ($[45] !== selectedTextAlign || $[46] !== t21) {
      t12 = <select value={selectedTextAlign} onChange={t21}>{t22}</select>;
      $[45] = selectedTextAlign;
      $[46] = t21;
      $[47] = t12;
    } else {
      t12 = $[47];
    }
    let t23;
    if ($[48] !== editor || $[49] !== selectedColor) {
      t23 = TEXT_COLORS.map(t24 => {
        const [label, color_0] = t24;
        return <button key={label} className={cn("font-800", {
          underline: selectedColor === label
        })} style={{
          color: color_0
        }} type="button" onClick={() => editor?.chain().focus().setColor(color_0).run()}>A</button>;
      });
      $[48] = editor;
      $[49] = selectedColor;
      $[50] = t23;
    } else {
      t23 = $[50];
    }
    if ($[51] !== t23) {
      t13 = <section className="flex gap-8 rounded-xsmall border-1 border-Gray-600 px-8 py-2">{t23}</section>;
      $[51] = t23;
      $[52] = t13;
    } else {
      t13 = $[52];
    }
    let t24;
    if ($[53] !== editor || $[54] !== selectedFontWeight) {
      t24 = FONT_WEIGHTS.map(fontWeight_0 => <button key={fontWeight_0} className={cn(fontWeight_0, {
        underline: selectedFontWeight === fontWeight_0
      })} type="button" onClick={() => editor?.chain().focus().setFontWeight(fontWeight_0).run()}>B</button>);
      $[53] = editor;
      $[54] = selectedFontWeight;
      $[55] = t24;
    } else {
      t24 = $[55];
    }
    if ($[56] !== t24) {
      t14 = <section className="flex gap-8 rounded-xsmall border-1 border-Gray-600 px-8 py-2">{t24}</section>;
      $[56] = t24;
      $[57] = t14;
    } else {
      t14 = $[57];
    }
    if ($[58] !== editor) {
      t15 = HIGHLIGHTS.map(highlight => {
        const isActive = editor.isActive("highlight", {
          highlight
        });
        return <button key={highlight} className={cn(highlight, "flex size-24 min-h-24 min-w-24 items-center justify-center rounded-xsmall font-700", {
          "border-1 border-Gray-800": isActive
        })} type="button" onClick={() => isActive ? editor.chain().focus().unsetHighlight().run() : editor.chain().focus().setHighlight(highlight).run()} />;
      });
      $[58] = editor;
      $[59] = t15;
    } else {
      t15 = $[59];
    }
    t7 = cn("box-border flex size-24 items-center justify-center", {
      "border-Gray-800 border-1 rounded-xxsmall": isBoxedText
    });
    $[12] = editor;
    $[13] = selectedColor;
    $[14] = selectedFontSize;
    $[15] = selectedFontWeight;
    $[16] = selectedHeading;
    $[17] = isBoxedText;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    isBoxedText = $[17];
    t10 = $[18];
    t11 = $[19];
    t12 = $[20];
    t13 = $[21];
    t14 = $[22];
    t15 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  let t16;
  if ($[60] !== editor || $[61] !== isBoxedText) {
    t16 = () => isBoxedText ? editor.chain().focus().unsetBoxedText().run() : editor.chain().focus().setBoxedText().run();
    $[60] = editor;
    $[61] = isBoxedText;
    $[62] = t16;
  } else {
    t16 = $[62];
  }
  let t17;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <BoxedTextIcon />;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] !== t16 || $[65] !== t7) {
    t18 = <button className={t7} type="button" onClick={t16}>{t17}</button>;
    $[64] = t16;
    $[65] = t7;
    $[66] = t18;
  } else {
    t18 = $[66];
  }
  let t19;
  if ($[67] !== editor) {
    t19 = () => {
      editor?.chain().focus().setHorizontalRule().run();
    };
    $[67] = editor;
    $[68] = t19;
  } else {
    t19 = $[68];
  }
  let t20;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <HrIcon />;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  let t21;
  if ($[70] !== t19) {
    t21 = <button type="button" onClick={t19}>{t20}</button>;
    $[70] = t19;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  let t22;
  if ($[72] !== imageUploadOptions) {
    t22 = <ImageButton {...imageUploadOptions} />;
    $[72] = imageUploadOptions;
    $[73] = t22;
  } else {
    t22 = $[73];
  }
  let t23;
  if ($[74] !== handleLinkButtonClick) {
    t23 = <Button dimensions="H26" roundness="rectangle" onClick={handleLinkButtonClick}>링크</Button>;
    $[74] = handleLinkButtonClick;
    $[75] = t23;
  } else {
    t23 = $[75];
  }
  let t24;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => setIsLinkModalOpened(false);
    $[76] = t24;
  } else {
    t24 = $[76];
  }
  let t25;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = e_2 => {
      setCustomLinkOptions(prev_0 => ({
        ...prev_0,
        href: e_2.target.value
      }));
    };
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  let t26;
  if ($[78] !== customLinkOptions.href) {
    t26 = <InputDecorator label={"\uC8FC\uC18C"}><Input value={customLinkOptions.href} onChange={t25} /></InputDecorator>;
    $[78] = customLinkOptions.href;
    $[79] = t26;
  } else {
    t26 = $[79];
  }
  let t27;
  let t28;
  let t29;
  let t30;
  let t31;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = value_0 => {
      setCustomLinkOptions(prev_1 => ({
        ...prev_1,
        theme: value_0
      }));
    };
    t28 = <Radio.Item value="text">문자</Radio.Item>;
    t29 = <Radio.Item value="primary-fill-button">버튼 1</Radio.Item>;
    t30 = <Radio.Item value="sub-fill-button">버튼 2</Radio.Item>;
    t31 = <Radio.Item value="image">이미지</Radio.Item>;
    $[80] = t27;
    $[81] = t28;
    $[82] = t29;
    $[83] = t30;
    $[84] = t31;
  } else {
    t27 = $[80];
    t28 = $[81];
    t29 = $[82];
    t30 = $[83];
    t31 = $[84];
  }
  let t32;
  if ($[85] !== customLinkOptions.theme) {
    t32 = <InputDecorator label={"\uB9C1\uD06C \uD615\uC2DD"}><Radio.Group value={customLinkOptions.theme} onChange={t27}>{t28}{t29}{t30}{t31}</Radio.Group></InputDecorator>;
    $[85] = customLinkOptions.theme;
    $[86] = t32;
  } else {
    t32 = $[86];
  }
  const t33 = customLinkOptions.isOutLink ? "out" : "in";
  let t34;
  let t35;
  let t36;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = value_1 => {
      setCustomLinkOptions(prev_2 => ({
        ...prev_2,
        isOutLink: value_1 === "out"
      }));
    };
    t35 = <Radio.Item value="out">외부에서 열기</Radio.Item>;
    t36 = <Radio.Item value="in">내부에서 열기</Radio.Item>;
    $[87] = t34;
    $[88] = t35;
    $[89] = t36;
  } else {
    t34 = $[87];
    t35 = $[88];
    t36 = $[89];
  }
  let t37;
  if ($[90] !== t33) {
    t37 = <InputDecorator label={"\uCC3D \uC5F4\uAE30"}><Radio.Group value={t33} onChange={t34}>{t35}{t36}</Radio.Group></InputDecorator>;
    $[90] = t33;
    $[91] = t37;
  } else {
    t37 = $[91];
  }
  let t38;
  if ($[92] !== t26 || $[93] !== t32 || $[94] !== t37) {
    t38 = <article className="mb-32 flex flex-col gap-16">{t26}{t32}{t37}</article>;
    $[92] = t26;
    $[93] = t32;
    $[94] = t37;
    $[95] = t38;
  } else {
    t38 = $[95];
  }
  const t39 = !customLinkOptions.href;
  let t40;
  if ($[96] !== customLinkOptions || $[97] !== editor) {
    t40 = () => {
      setIsLinkModalOpened(false);
      editor.chain().focus().setCustomLink(customLinkOptions).run();
    };
    $[96] = customLinkOptions;
    $[97] = editor;
    $[98] = t40;
  } else {
    t40 = $[98];
  }
  let t41;
  if ($[99] !== t39 || $[100] !== t40) {
    t41 = <Button className="flex-1" disabled={t39} onClick={t40}>설정</Button>;
    $[99] = t39;
    $[100] = t40;
    $[101] = t41;
  } else {
    t41 = $[101];
  }
  let t42;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <Button className="flex-1" theme="sub-fill" onClick={() => {
      setIsLinkModalOpened(false);
    }}>취소</Button>;
    $[102] = t42;
  } else {
    t42 = $[102];
  }
  let t43;
  if ($[103] !== t41) {
    t43 = <footer className="flex w-full gap-8">{t41}{t42}</footer>;
    $[103] = t41;
    $[104] = t43;
  } else {
    t43 = $[104];
  }
  let t44;
  if ($[105] !== isLinkModalOpened || $[106] !== t38 || $[107] !== t43) {
    t44 = <Modal opened={isLinkModalOpened} onClose={t24}>{t38}{t43}</Modal>;
    $[105] = isLinkModalOpened;
    $[106] = t38;
    $[107] = t43;
    $[108] = t44;
  } else {
    t44 = $[108];
  }
  let t45;
  if ($[109] !== children || $[110] !== t10 || $[111] !== t11 || $[112] !== t12 || $[113] !== t13 || $[114] !== t14 || $[115] !== t15 || $[116] !== t18 || $[117] !== t21 || $[118] !== t22 || $[119] !== t23 || $[120] !== t44 || $[121] !== t8 || $[122] !== t9) {
    t45 = <nav className={t8}>{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t18}{t21}{t22}{t23}{t44}{children}</nav>;
    $[109] = children;
    $[110] = t10;
    $[111] = t11;
    $[112] = t12;
    $[113] = t13;
    $[114] = t14;
    $[115] = t15;
    $[116] = t18;
    $[117] = t21;
    $[118] = t22;
    $[119] = t23;
    $[120] = t44;
    $[121] = t8;
    $[122] = t9;
    $[123] = t45;
  } else {
    t45 = $[123];
  }
  return t45;
};
function _temp(option) {
  return <option key={option.value} value={option.value}>{option.label}</option>;
}
function _temp2(fontSize_0) {
  return <option key={fontSize_0} className={fontSize_0} value={fontSize_0}>{fontSize_0}</option>;
}
function _temp3(textAlign_0) {
  return <option key={textAlign_0} value={textAlign_0}>{textAlign_0}</option>;
}